import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons/faRotateRight';

import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { CompletedStepCheckmark } from 'ui-components/components/completed-step-checkmark/completed-step-checkmark';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { DataTable } from '../../../../components/modals/label-batch-status/error-warn/data-table';
import { Steps } from '../context/constants';
import { useStepInfo, useReadyToPickContext } from '../context/ready-to-pick-context';
import { messages } from '../messages';
import style from '../ready-to-pick.module.less';

/**
 * @returns {JSX.Element}
 */
export const VerifyInventoryCard = () => {
  const {
    areAllProblemOrdersSelected,
    areAnyProblemOrdersSelected,
    canVerifyInventoryContinue,
    onVerifyInventoryContinue,
    prepareVerifyInventoryStep,
    problemOrders,
    selectedProblemOrderIds,
    removeProblemOrder,
    removeSelectedProblemOrder,
    toggleAllOrdersSelections,
    toggleOrderSelection,
  } = useReadyToPickContext();

  const { isActiveStep, isLoading, isStepComplete } = useStepInfo(Steps.checkForInventoryErrors);

  const onRetry = useCallback(() => {
    const isRetry = true;
    prepareVerifyInventoryStep(isRetry);
  }, [prepareVerifyInventoryStep]);

  const problemOrdersWithSelected = useMemo(() => {
    return problemOrders.map((order) => ({
      ...order,
      isSelected: selectedProblemOrderIds.includes(order.fulfillmentPlanId),
    }));
  }, [problemOrders, selectedProblemOrderIds]);

  return (
    <Card
      className={classnames(style['step-card'], {
        [style.inactive]: !isActiveStep,
      })}
    >
      <FlexRow className={style['cell-row']} gap={Gap.Space16}>
        <FlexColumn className={style['card-icon']}>
          <CompletedStepCheckmark isComplete={isStepComplete} />
        </FlexColumn>
        <FlexColumn className={style.column} gap={Gap.Space16}>
          <FlexRow className={style['split-title']}>
            <Text textType={TextType.H3} value={messages.checkForInventoryErrors} />
            {isActiveStep ? (
              <Button className={style['header-button']} disabled={isLoading} onClick={onRetry} theme={THEME_CONSTANTS.link}>
                <FlexRow className={style['header-button']} gap={Gap.Space4}>
                  <FontAwesomeIcon className={style['button-icon']} icon={faRotateRight} />
                  <Text element='span' textType={TextType.Caption} value={messages.retryAllocation} />
                </FlexRow>
              </Button>
            ) : null}
          </FlexRow>
          {isActiveStep && !isLoading ? (
            <>
              <Text textType={TextType.Description} value={messages.checkForInventoryErrorsBody} />
              <span className={style['order-errors']}>
                <Button disabled={!areAnyProblemOrdersSelected} onClick={removeSelectedProblemOrder} theme={THEME_CONSTANTS.primary}>
                  <FormattedMessage {...messages.orderErrorsRemoveButton} />
                </Button>
              </span>
              <DataTable
                allRemoved={false}
                className={style['data-table']}
                errorOrWarnCollection={problemOrdersWithSelected}
                hasToggleAllChecked={areAllProblemOrdersSelected}
                onRemoveRow={removeProblemOrder}
                onRowSelected={toggleOrderSelection}
                onToggleAllChecked={toggleAllOrdersSelections}
                showReadyToPickTableLayout
                showSkuCell
              />
              <span>
                <Button disabled={!canVerifyInventoryContinue} onClick={onVerifyInventoryContinue} theme={THEME_CONSTANTS.primary}>
                  <FormattedMessage {...messages.continue} />
                </Button>
              </span>
            </>
          ) : null}
          {isActiveStep && isLoading ? <LoadingIndicator className={style.loading} /> : null}
        </FlexColumn>
      </FlexRow>
    </Card>
  );
};
